import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, ArrowLeft, LogOut, Loader2, TrendingDown, Users, ShieldCheck, Handshake, ClipboardCheck,
} from 'lucide-react';
import { BACKEND_URL } from '../config/api';

/**
 * Phase 5.2 launch conversion funnel — admin-only.
 * Consumes /api/admin/funnel/tradesperson-conversion for every metric.
 * The frontend never recomputes counts or derives conversion — it
 * only renders whatever the backend returned.
 */
const WINDOWS = [
  { key: '7d', label: '7 days', testId: 'funnel-window-7d' },
  { key: '30d', label: '30 days', testId: 'funnel-window-30d' },
  { key: '90d', label: '90 days', testId: 'funnel-window-90d' },
  { key: 'all', label: 'All time', testId: 'funnel-window-all' },
];

const STAGE_ICONS = {
  signed_up: Users,
  onboarding_completed: ClipboardCheck,
  became_searchable: ShieldCheck,
  accepted_hire: Handshake,
};

export default function AdminFunnel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [window, setWindow] = useState('30d');
  const [trade, setTrade] = useState('');
  const [region, setRegion] = useState('');
  const [tradeOptions, setTradeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((r) => { if (r.data.user_type !== 'admin') navigate('/login'); else setUser(r.data); })
      .catch(() => navigate('/login'));
    axios.get(`${BACKEND_URL}/api/trades`).then((r) => setTradeOptions(r.data || [])).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const params = new URLSearchParams({ window });
    if (trade) params.append('trade', trade);
    if (region.trim()) params.append('region', region.trim());
    axios.get(`${BACKEND_URL}/api/admin/funnel/tradesperson-conversion?${params.toString()}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load funnel'))
      .finally(() => setLoading(false));
  }, [user, window, trade, region]);

  const topCount = useMemo(() => data?.stages?.[0]?.count || 0, [data]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-funnel-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Launch Conversion Funnel</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button
              onClick={async () => { try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } finally { navigate('/login'); } }}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Cohort switcher */}
        <div className="card">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Funnel cohort">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                role="tab"
                aria-selected={window === w.key}
                onClick={() => setWindow(w.key)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  window === w.key
                    ? 'bg-orange-50 text-orange-700 border-orange-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={w.testId}
              >
                {w.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Trade</label>
              <select
                value={trade}
                onChange={(e) => setTrade(e.target.value)}
                className="w-full mt-1"
                data-testid="funnel-trade-filter"
              >
                <option value="">All trades</option>
                {tradeOptions.map((t) => (
                  <option key={t.trade_id || t.slug} value={t.slug || t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Region (postcode)</label>
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. SW1"
                className="w-full mt-1"
                data-testid="funnel-region-filter"
              />
            </div>
            <div className="flex items-end">
              <p className="text-xs text-slate-500" data-testid="funnel-generated-at">
                {data?.generated_at && `Updated ${new Date(data.generated_at).toLocaleString()}`}
              </p>
            </div>
          </div>
        </div>

        {/* Stages */}
        {loading ? (
          <div className="card py-12 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading funnel…
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="funnel-stage-grid">
              {data.stages.map((s, idx) => {
                const Icon = STAGE_ICONS[s.key] || Users;
                return (
                  <div
                    key={s.key}
                    className="card"
                    data-testid={`funnel-stage-${s.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-orange-500" />
                      <span className="text-sm font-medium text-slate-600">{s.label}</span>
                    </div>
                    <p
                      className="text-3xl font-bold text-slate-900 mt-3"
                      data-testid={`funnel-stage-count-${s.key}`}
                    >
                      {s.count}
                    </p>
                    <div className="flex items-baseline gap-3 mt-2 text-xs">
                      {idx > 0 ? (
                        <>
                          <span
                            className="text-emerald-600"
                            data-testid={`funnel-stage-conv-${s.key}`}
                          >
                            {s.conversion_from_prev_pct}% from prev
                          </span>
                          <span
                            className="text-rose-500 flex items-center gap-1"
                            data-testid={`funnel-stage-drop-${s.key}`}
                          >
                            <TrendingDown className="h-3 w-3" /> {s.drop_off_from_prev} dropped
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">Top of funnel</span>
                      )}
                    </div>
                    {idx > 0 && topCount > 0 && (
                      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-orange-500"
                          style={{ width: `${Math.min(100, s.conversion_from_top_pct)}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current-state sidebar */}
            <div className="card" data-testid="funnel-current-state">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Current searchable total</h3>
              <p className="text-2xl font-bold text-slate-900" data-testid="funnel-current-searchable-count">
                {data.current_searchable_total}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Live count of tradespeople with `is_searchable=true` right now.
                Separate from the funnel above, which counts users who EVER became searchable.
              </p>
            </div>

            {/* Invariant violations — surfaced verbatim from the backend
                so admins can spot data drift the server-side nested
                intersection had to work around. Empty state means clean. */}
            {data.invariant_violations && data.invariant_violations.length > 0 && (
              <div
                className="rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5"
                data-testid="funnel-invariant-violations"
              >
                <h3 className="text-sm font-semibold text-amber-900">
                  Data drift detected ({data.invariant_violations.length})
                </h3>
                <p className="text-xs text-amber-800 mt-1">
                  Stage counts above are correct — these rows were excluded
                  because they broke a nesting invariant. Investigate and
                  fix at the source rather than clamping here.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-amber-900">
                  {data.invariant_violations.map((v, i) => (
                    <li
                      key={`${v.stage}-${i}`}
                      data-testid={`funnel-violation-${v.stage}-${i}`}
                    >
                      <strong>{v.stage}:</strong> {v.violation} — {v.count} row(s)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Metric definitions */}
            <details className="card" data-testid="funnel-definitions">
              <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                Metric definitions
              </summary>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {data.stages.map((s) => (
                  <li key={s.key}><strong className="text-slate-900">{s.label}</strong> — {s.definition}</li>
                ))}
              </ul>
            </details>
          </>
        ) : null}
      </main>
    </div>
  );
}
